import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api from "../../api";
import { PageTitle, TH, TD } from "./AdminLayout";

const tooltipStyle = {
  contentStyle: { background: "#0F0F14", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 },
  labelStyle: { color: "#A1A1AA" },
};

export default function Analytics() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <p className="text-white/40 font-mono text-sm">Crunching numbers…</p>;

  const daily = data.daily || [];
  const peak = daily.length ? daily.reduce((m, d) => (d.commands > m.commands ? d : m), daily[0]) : null;
  const avgMinutes = daily.length ? daily.reduce((a, d) => a + d.session_minutes, 0) / daily.length : 0;

  const exportData = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "groovemusic-analytics.json";
    a.click();
  };

  return (
    <div data-testid="admin-analytics">
      <PageTitle title="Analytics" sub="Growth, engagement and listening patterns — last 30 days.">
        <button onClick={exportData} data-testid="analytics-export" className="inline-flex items-center gap-2 text-xs font-semibold border border-white/15 hover:border-blurple/50 hover:text-white text-white/60 rounded-full px-4 py-2 transition-colors duration-150">
          <Download size={13} /> Export JSON
        </button>
      </PageTitle>

      <div className="grid grid-cols-2 gap-4 mb-6 max-w-2xl">
        <div className="rounded-lg border border-white/5 bg-panel p-5" data-testid="analytics-avg-session">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold">Avg listening / server / day</p>
          <p className="font-heading text-2xl font-bold text-white mt-2">
            {Math.round(avgMinutes / 60)}h{" "}
            {Math.round(avgMinutes % 60)}m
          </p>
        </div>
        <div className="rounded-lg border border-white/5 bg-panel p-5" data-testid="analytics-peak">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold">Peak usage day</p>
          {peak ? (
            <>
              <p className="font-heading text-2xl font-bold text-white mt-2">{peak.date.slice(5)}</p>
              <p className="text-xs text-white/40 font-mono mt-1">{peak.commands.toLocaleString()} commands</p>
            </>
          ) : (
            <p className="text-sm text-white/40 mt-2">No data yet — waiting for the bot's first sync.</p>
          )}
        </div>
      </div>

      {!daily.length && (
        <div className="rounded-lg border border-white/10 bg-white/[0.03] px-5 py-4 mb-6 text-sm text-white/50" data-testid="analytics-empty">
          Charts fill in automatically once the bot starts syncing (every 5 minutes while it runs).
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <div className="rounded-lg border border-white/5 bg-panel p-5" data-testid="chart-servers">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold mb-4">Server growth</p>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data.daily}>
              <defs>
                <linearGradient id="gs" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#5865F2" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#5865F2" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.04)" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#55556a" }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 10, fill: "#55556a" }} domain={["dataMin - 50", "auto"]} width={55} />
              <Tooltip {...tooltipStyle} />
              <Area type="monotone" dataKey="servers" stroke="#5865F2" fill="url(#gs)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-lg border border-white/5 bg-panel p-5" data-testid="chart-songs">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold mb-4">Songs played / day</p>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data.daily}>
              <defs>
                <linearGradient id="gp" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#06B6D4" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#06B6D4" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.04)" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#55556a" }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 10, fill: "#55556a" }} domain={["dataMin - 500", "auto"]} width={55} />
              <Tooltip {...tooltipStyle} />
              <Area type="monotone" dataKey="songs" stroke="#06B6D4" fill="url(#gp)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-lg border border-white/5 bg-panel p-5" data-testid="chart-active">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold mb-4">Daily active servers</p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.daily}>
              <CartesianGrid stroke="rgba(255,255,255,0.04)" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#55556a" }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 10, fill: "#55556a" }} domain={["dataMin - 100", "auto"]} width={50} />
              <Tooltip {...tooltipStyle} />
              <Bar dataKey="active_servers" fill="#5865F2" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-lg border border-white/5 bg-panel p-5" data-testid="chart-commands">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold mb-4">Most-used commands</p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.top_commands} layout="vertical">
              <CartesianGrid stroke="rgba(255,255,255,0.04)" />
              <XAxis type="number" tick={{ fontSize: 10, fill: "#55556a" }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#A1A1AA", fontFamily: "JetBrains Mono" }} width={90} tickFormatter={(n) => `!${n}`} />
              <Tooltip {...tooltipStyle} />
              <Bar dataKey="uses" fill="#06B6D4" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-lg border border-white/5 bg-panel overflow-hidden" data-testid="analytics-top-songs">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold px-5 py-4">Most-played songs</p>
          <table className="w-full">
            <thead className="border-t border-white/5"><tr><TH>#</TH><TH>Track</TH><TH>Plays</TH></tr></thead>
            <tbody className="divide-y divide-white/5">
              {data.top_songs.map((s, i) => (
                <tr key={s.title}>
                  <TD className="font-mono text-white/30">{i + 1}</TD>
                  <TD><span className="text-white">{s.title}</span> <span className="text-white/40">— {s.artist}</span></TD>
                  <TD className="font-mono">{s.plays.toLocaleString()}</TD>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="rounded-lg border border-white/5 bg-panel overflow-hidden" data-testid="analytics-top-artists">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold px-5 py-4">Most-popular artists</p>
          <table className="w-full">
            <thead className="border-t border-white/5"><tr><TH>#</TH><TH>Artist</TH><TH>Plays</TH></tr></thead>
            <tbody className="divide-y divide-white/5">
              {data.top_artists.map((a, i) => (
                <tr key={a.name}>
                  <TD className="font-mono text-white/30">{i + 1}</TD>
                  <TD className="text-white">{a.name}</TD>
                  <TD className="font-mono">{a.plays.toLocaleString()}</TD>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
